import type { PaymentMethod } from "@/context/FinanceContext";
import { PersistedState } from "./useFinanceState";
import { toast } from "@/hooks/use-toast";

export function usePaymentMethodsOperations(
  state: PersistedState,
  setState: React.Dispatch<React.SetStateAction<PersistedState>>,
  user: any,
  createPaymentMethod: (data: any) => Promise<string>,
  updateSupabasePaymentMethod: (id: string, data: any) => Promise<boolean>,
  deletePaymentMethod: (id: string) => Promise<boolean>
) {
  const addPaymentMethod = async (name: string): Promise<string | null> => {
    // Check for duplicate names
    const existingPaymentMethod = state.paymentMethods.find(p => p.name.toLowerCase() === name.toLowerCase());
    if (existingPaymentMethod) {
      toast({
        variant: "destructive",
        title: "Nome duplicado",
        description: "Já existe uma forma de pagamento com este nome.",
      });
      return null;
    }

    const tempId = `pm-${crypto.randomUUID()}`;
    
    // Sync to Supabase if user is logged in
    if (user) {
      try {
        const supabaseId = await createPaymentMethod({ name });
        if (supabaseId) {
          // Update local state with the real ID from Supabase
          setState((s) => ({
            ...s,
            paymentMethods: [...s.paymentMethods, { id: supabaseId, name }],
          }));
          return supabaseId;
        }
      } catch (error) {
        console.error('Error syncing payment method to Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível salvar a forma de pagamento na nuvem.",
        });
        return null;
      }
    } else {
      // Update local state with temp ID if not logged in
      setState((s) => ({
        ...s,
        paymentMethods: [...s.paymentMethods, { id: tempId, name }],
      }));
      return tempId;
    }
    return null;
  };

  const updatePaymentMethod = async (id: string, name: string) => {
    // Update local state first
    setState((s) => ({
      ...s,
      paymentMethods: s.paymentMethods.map((p) => (p.id === id ? { ...p, name } : p)),
    }));

    // Sync to Supabase if user is logged in
    if (user) {
      try {
        await updateSupabasePaymentMethod(id, { name });
      } catch (error) {
        console.error('Error syncing payment method update to Supabase:', error);
      }
    }
  };

  const removePaymentMethod = async (id: string) => {
    // Sync to Supabase if user is logged in first
    if (user) {
      try {
        const success = await deletePaymentMethod(id);
        if (!success) {
          // Se a exclusão no Supabase falhou, não remove do estado local
          return;
        }
        
        toast({
          title: "Forma de pagamento excluída",
          description: "A forma de pagamento foi removida com sucesso.",
        });
      } catch (error) {
        console.error('Error deleting payment method from Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível excluir a forma de pagamento. Tente novamente.",
        });
        return;
      }
    }

    // Update local state only after successful Supabase deletion (or if offline)
    setState((s) => ({
      ...s,
      paymentMethods: s.paymentMethods.filter((p) => p.id !== id),
    }));

    if (!user) {
      toast({
        title: "Forma de pagamento excluída localmente",
        description: "A forma de pagamento foi removida localmente. Faça login para sincronizar.",
      });
    }
  };

  return {
    addPaymentMethod,
    updatePaymentMethod,
    removePaymentMethod,
  };
}
